import { useState, useCallback } from "react";
import Cookies from "js-cookie";
import useSubscriptionSummary from "../../hooks/useSubscriptionSummary";
import {
	SURVEY_NOTIFICATION_COOKIE,
	SURVEY_COOKIE_EXPIRY_DAYS,
	ENABLE_SURVEY,
	SURVEY_RUNS_UNTIL,
} from "../../config/surveyConfig";

/**
 * Hook that determines whether the user is eligible to see the survey notification.
 *
 * Eligibility requires:
 * - ENABLE_SURVEY is true
 * - Survey has not expired (SURVEY_RUNS_UNTIL)
 * - User has not dismissed the survey (cookie)
 * - User has 2+ active subscription licenses
 *
 * @returns {{ isEligible: boolean, isLoading: boolean, dismiss: Function }} Survey eligibility state.
 */
const useSurveyEligibility = () => {
	const [ isDismissed, setIsDismissed ] = useState( () => !! Cookies.get( SURVEY_NOTIFICATION_COOKIE ) );

	// The licence total comes from the server now, which counts pending-cancel subscriptions alongside
	// active ones — a licence being wound down is still a licence held — so the threshold is reached
	// slightly more often than before.
	const { summary, isLoading } = useSubscriptionSummary();
	const hasEnoughLicenses = summary.totalActiveLicenses >= 2;

	const isExpired = SURVEY_RUNS_UNTIL && new Date() > SURVEY_RUNS_UNTIL;
	const isEligible = ENABLE_SURVEY && ! isExpired && ! isDismissed && hasEnoughLicenses;

	// Note: SurveyNotification also writes this cookie internally on dismiss.
	// The double-write is intentional so SurveyNotification keeps working standalone.
	const dismiss = useCallback( () => {
		Cookies.set( SURVEY_NOTIFICATION_COOKIE, "true", { expires: SURVEY_COOKIE_EXPIRY_DAYS } );
		setIsDismissed( true );
	}, [] );

	return { isEligible, isLoading, dismiss };
};

export default useSurveyEligibility;
